import {Component, inject} from '@angular/core';
import {NgFor, NgIf} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {TranslocoDirective, TranslocoService} from '@jsverse/transloco';
import {NzDividerModule} from 'ng-zorro-antd/divider';
import {NzTableModule} from 'ng-zorro-antd/table';
import {DatePipe} from '@angular/common';
import {NzPaginationComponent} from 'ng-zorro-antd/pagination';
import {ASSET_CONSTANTS} from '../../../shared/constants/asset-constants';
import {
  MbButtonComponent, MbDocumentViewerComponent,
  MbModalComponent, MbSearchComponent,
  MbSelectComponent, MbSkeletonComponent,
  ToastService, MbEmptyListComponent
} from '../../../components/common';
import {
  ITipOffsCategoriesResponse,
  ITipOffsCategory,
  TipOffsCategoriesService
} from '../../../core/services/tip-offs-categories.service';
import {
  TipoffsService, LocationsService,
  ITipoffsContent, IContent,
} from '../../../core/services';
import {TruncatePipe} from '../../../shared/pipes/truncate.pipe';


@Component({
  selector: 'app-tipoffs-overview-page',
  imports: [
    MbSearchComponent,
    TranslocoDirective,
    NzDividerModule,
    MbEmptyListComponent,
    NzTableModule,
    DatePipe,
    NgFor,
    NzPaginationComponent,
    NgIf,
    MbSkeletonComponent,
    MbModalComponent,
    MbDocumentViewerComponent,
    MbSelectComponent,
    MbButtonComponent,
    FormsModule,
    TruncatePipe,
  ],
  templateUrl: './tipoffs-overview-page.component.html',
})
export class TipoffsOverviewPageComponent {
  protected readonly ASSET_CONSTANTS = ASSET_CONSTANTS;

  private toastService = inject(ToastService);
  private tipOffService = inject(TipoffsService);
  private locationsService = inject(LocationsService);
  private translocoService = inject(TranslocoService);
  private tipOffCategoryService = inject(TipOffsCategoriesService);

  tipOffs: ITipoffsContent[] = [];
  totalItems = 0;
  pageSize = 8;
  currentPage = 1;
  searchValue = '';

  isTipOffModalVisible = false;
  isLoading = false;
  isLoadingTipOffDetail = false;
  isFilterModalVisible = false;

  selectedTipOff: ITipoffsContent | null = null;

  categoryOptions: { value: string; label: string }[] = [];
  districtOptions: { value: string; label: string }[] = [];
  sectorOptions: { value: string; label: string }[] = [];
  cellOptions: { value: string; label: string }[] = [];
  villageOptions: { value: string; label: string }[] = [];

  activeLang = 'en';
  filterOptions: Record<string, string> = {};

  tipOffFilterForm = {
    district: '',
    sector: '',
    cell: '',
    village: '',
    category: '',
  };

  constructor() {
    this.loadTipOffs(this.currentPage, this.pageSize, this.searchValue);
    this.loadDistricts();
    this.loadTipOffCategories();
    this.activeLang = this.translocoService.getActiveLang();
  }

  loadTipOffCategories(): void {
    this.tipOffCategoryService
      .getTipOffsCategories(0, 20, true)
      .subscribe({
        next: (response) => {
          this.categoryOptions = [
            {value: '', label: this.translocoService.translate('all')},
            ...response.payload.content.map((type: ITipOffsCategory) => {
              const typeData = type as ITipOffsCategory & { nameEn?: string; nameFr?: string };
              let label = type.name;

              if (this.activeLang === 'en' && typeData.nameEn) {
                label = typeData.nameEn;
              } else if (this.activeLang === 'fr' && typeData.nameFr) {
                label = typeData.nameFr;
              }

              return {
                value: type.id,
                label,
              };
            })
          ];
        },
        error: (error: ITipOffsCategoriesResponse) => {
          this.toastService.show(error.message, 'error');
        },
      });
  }

  loadTipOffs(currentPage: number, pageSize: number, searchValue?: string, filterOptions?: Record<string, string>) {
    this.isLoading = true;
    this.tipOffService
      .getTipoffs(currentPage - 1, pageSize, searchValue, filterOptions)
      .subscribe((response) => {
        this.tipOffs = response.content;
        this.totalItems = response.page.totalElements;
        this.isLoading = false;
      });
  }

  onSearch(): void {
    if (!this.searchValue || this.searchValue.trim() === '') {
      this.loadTipOffs(this.currentPage, this.pageSize, this.searchValue);
      return;
    }
    this.currentPage = 1;
    this.loadTipOffs(this.currentPage, this.pageSize, this.searchValue);
  }

  onClearSearch(): void {
    this.searchValue = '';
    this.loadTipOffs(this.currentPage, this.pageSize, this.searchValue);
  }

  onTipOffsPageChange(page: number): void {
    this.currentPage = page;
    this.loadTipOffs(page, this.pageSize, this.searchValue, this.filterOptions);
  }

  onTipOffModalCancelAction(): void {
    this.isTipOffModalVisible = false;
    this.selectedTipOff = null;
  }

  onTipOffModalOkAction(): void {
    this.isTipOffModalVisible = false;
    this.selectedTipOff = null;
  }

  onTipOffClick(id: string): void {
    this.isTipOffModalVisible = true;
    this.isLoadingTipOffDetail = true;
    this.tipOffService.getTipoff(id).subscribe({
      next: (response) => {
        this.selectedTipOff = response;
        this.isLoadingTipOffDetail = false;
      },
      error: (error) => {
        this.toastService.show(error.message || error.error.message, 'error');
        this.isLoadingTipOffDetail = false;
      },
    });
  }

  loadDistricts(): void {
    this.locationsService.getDistricts().subscribe({
      next: (response) => {
        this.districtOptions = [
          {value: '', label: this.translocoService.translate('all')},
          ...response.payload.map((district: IContent) => ({
            value: district.id,
            label: district.name,
          }))
        ];
      },
    });
  }

  loadSectors(districtId: string): void {
    if (!districtId) {
      this.sectorOptions = [{value: '', label: this.translocoService.translate('all')}];
      return;
    }

    this.locationsService.getSectors(districtId).subscribe({
      next: (response) => {
        this.sectorOptions = [
          {value: '', label: this.translocoService.translate('all')},
          ...response.payload.content.map((sector: IContent) => ({
            value: sector.id,
            label: sector.name,
          }))
        ];
      },
    });
  }

  loadCells(sectorId: string): void {
    if (!sectorId) {
      this.cellOptions = [{value: '', label: this.translocoService.translate('all')}];
      return;
    }


    this.locationsService.getCells(sectorId).subscribe({
      next: (response) => {
        this.cellOptions = [
          {value: '', label: 'All'},
          ...response.payload.content.map((cell: IContent) => ({
            value: cell.id,
            label: cell.name,
          }))
        ];
      },
    });
  }

  loadVillages(cellId: string): void {
    if (!cellId) {
      this.villageOptions = [{value: '', label: this.translocoService.translate('all')}];
      return;
    }

    this.locationsService.getVillages(cellId).subscribe({
      next: (response) => {
        this.villageOptions = [
          {value: '', label: this.translocoService.translate('all')},
          ...response.payload.content.map((village: IContent) => ({
            value: village.id,
            label: village.name,
          }))
        ];
      },
    });
  }

  showFilterModal(): void {
    this.isFilterModalVisible = true;
  }

  handleFilterModalCancel(): void {
    this.isFilterModalVisible = false;
  }

  handleFilterChange(type: string, event: string): void {
    const isEmpty = event === 'all' || event === '' || event === null || event === undefined;

    if (isEmpty) {
      delete this.filterOptions[type];
    } else {
      this.filterOptions[type] = event;

      if (type === 'district') {
        this.loadSectors(event);
        delete this.filterOptions['sector'];
        delete this.filterOptions['cell'];
        delete this.filterOptions['village'];
        this.tipOffFilterForm.sector = '';
        this.tipOffFilterForm.cell = '';
        this.tipOffFilterForm.village = '';
      }

      if (type === 'sector') {
        this.loadCells(event);
        delete this.filterOptions['cell'];
        delete this.filterOptions['village'];
        this.tipOffFilterForm.cell = '';
        this.tipOffFilterForm.village = '';
      }

      if (type === 'cell') {
        this.loadVillages(event);
        delete this.filterOptions['village'];
        this.tipOffFilterForm.village = '';
      }
    }
    this.currentPage = 1;
  }

  handleFilterApply(): void {
    this.handleFilterModalCancel();
    this.filterOptions['categoryId'] = this.tipOffFilterForm.category;
    this.filterOptions['districtId'] = this.tipOffFilterForm.district;
    this.filterOptions['sectorId'] = this.tipOffFilterForm.sector;
    this.filterOptions['cellId'] = this.tipOffFilterForm.cell;
    this.filterOptions['villageId'] = this.tipOffFilterForm.village;
    this.loadTipOffs(this.currentPage, this.pageSize, this.searchValue, this.filterOptions);
  }

  handleFilterClear(): void {
    this.filterOptions = {};
    this.tipOffFilterForm.category = '';
    this.tipOffFilterForm.district = '';
    this.tipOffFilterForm.sector = '';
    this.tipOffFilterForm.cell = '';
    this.tipOffFilterForm.village = '';
    this.currentPage = 1;
    this.loadTipOffs(this.currentPage, this.pageSize, this.searchValue, this.filterOptions);
  }
}
